import { useState, useEffect, useCallback } from 'react';
import { getGlobalSettings, getSetting } from '../utils/globalSettingsService';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebaseClient';

/**
 * React Hook for Global Settings
 * 
 * Provides access to global settings with proper state management
 * and automatic updates when settings change.
 */

export const useGlobalSettings = () => {
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadSettings = async () => {
      try {
        setLoading(true);
        setError(null);
        const globalSettings = await getGlobalSettings();

        if (isMounted) {
          setSettings(globalSettings);
        }
      } catch (err) {
        console.error('Error loading global settings:', err);

        if (isMounted) {
          setError((err as any).message);
          // Use fallback settings with Stripe defaults
          const { defaultStripeSettings } = await import('../config/stripeConfig');
          setSettings({
            app: {
              name: process.env.REACT_APP_APP_NAME || 'App-Template',
              description: process.env.REACT_APP_APP_DESCRIPTION || 'Multi-tenant SaaS platform',
              version: process.env.REACT_APP_VERSION || '1.0.0',
              subtitle: process.env.REACT_APP_APP_SUBTITLE || 'Your framework to develop production ready applications',
              platformTagline: process.env.REACT_APP_PLATFORM_TAGLINE || 'Multi-tenant SaaS Platform',
              companyName: process.env.REACT_APP_COMPANY_NAME || 'Your Company'
            },
            features: {
              analytics: true,
              feedback: true,
              referrals: true,
              userRegistration: true,
              guestAccess: false,
              socialLogin: true,
              emailNotifications: true,
              exports: true,
              apiAccess: false,
              stripe: false
            },
            modules: {
              evaluationSchemas: {
                enabled: true,
                version: '1.0.0'
              },
              consultants: {
                enabled: true,
                version: '1.0.0'
              },
              presentations: {
                enabled: true,
                version: '1.0.0'
              },
              analytics: {
                enabled: true,
                version: '1.0.0'
              },
              reports: {
                enabled: true,
                version: '1.0.0'
              }
            },
            analytics: {
              enabled: true,
              provider: 'firebase',
              trackUserBehavior: true,
              anonymizeData: false
            },
            ui: {
              theme: {
                primary: '#2563eb',
                secondary: '#2563eb',
                background: '#f8fafc',
                text: '#111827'
              },
              layout: {
                sidebarWidth: 240,
                headerHeight: 64
              }
            },
            stripe: defaultStripeSettings
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  // Real-time listener for global settings changes - TEMPORARILY DISABLED
  // useEffect(() => {
  //   let unsubscribe = null;
  //   
  //   const setupRealtimeListener = async () => {
  //     try {
  //       // Find the global settings document
  //       const { collection, query, limit, getDocs } = await import('firebase/firestore');
  //       const settingsQuery = await getDocs(query(collection(db, 'globalSettings'), limit(1)));
  //       
  //       if (!settingsQuery.empty) {
  //         const settingsDoc = settingsQuery.docs[0];
  //         const docId = settingsDoc.id;
  //         
  //                     // Set up real-time listener
  //           unsubscribe = onSnapshot(doc(db, 'globalSettings', docId), (doc) => {
  //             if (doc.exists()) {
  //               const newSettings = doc.data();
  //               setSettings(newSettings);
  //             }
  //           }, (error) => {
  //             console.error('Error listening to global settings:', error);
  //           });
  //       }
  //     } catch (error) {
  //       console.error('Error setting up real-time listener:', error);
  //     }
  //   };

  //   setupRealtimeListener();
  //   
  //   return () => {
  //     if (unsubscribe) {
  //       unsubscribe();
  //     }
  //   };
  // }, []);

  // Periodic refresh as backup - TEMPORARILY DISABLED
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     refreshSettings();
  //   }, 120000); // 2 minutes
  //   
  //   return () => clearInterval(interval);
  // }, [refreshSettings]);

  const refreshSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const globalSettings = await getGlobalSettings(true); // Force refresh
      setSettings(globalSettings);
    } catch (err: any) {
      console.error('Error refreshing global settings:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    settings,
    loading,
    error,
    refreshSettings
  };
};

/**
 * Hook for specific app settings
 */
export const useAppSettings = () => {
  const { settings, loading, error, refreshSettings } = useGlobalSettings();

  // Handle both 'app' and 'appInfo' structures for compatibility
  const appInfo = settings?.appInfo || settings?.app || {};
  
  return {
    appName: appInfo?.name || process.env.REACT_APP_APP_NAME || 'App-Template',
    appDescription: appInfo?.description || process.env.REACT_APP_APP_DESCRIPTION || 'Multi-tenant SaaS platform',
    appVersion: appInfo?.version || process.env.REACT_APP_VERSION || '1.0.0',
    appSubtitle: appInfo?.subtitle || process.env.REACT_APP_APP_SUBTITLE || 'Your framework to develop production ready applications',
    platformTagline: appInfo?.platformTagline || process.env.REACT_APP_PLATFORM_TAGLINE || 'Multi-tenant SaaS Platform',
    companyName: appInfo?.companyName || process.env.REACT_APP_COMPANY_NAME || 'Your Company',
    features: settings?.features || {
      analytics: true,
      feedback: true,
      referrals: true
    },
    loading,
    error,
    refreshSettings
  };
};

/**
 * Hook for feature flags
 */
export const useFeatureFlags = () => {
  const { settings, loading, error, refreshSettings } = useGlobalSettings();

  const isFeatureEnabled = (featureName: string) => {
    if (!settings?.features) {
      return false;
    }
    return settings.features[featureName] === true;
  };

  return {
    features: settings?.features || {},
    isFeatureEnabled,
    loading,
    error,
    refreshSettings
  };
};

/**
 * Hook for module status
 */
export const useModules = () => {
  const { settings, loading, error, refreshSettings } = useGlobalSettings();

  const isModuleEnabled = (moduleName: string) => {
    if (!settings?.modules) {
      return false;
    }
    return settings.modules[moduleName]?.enabled === true;
  };

  const getModuleConfig = (moduleName: string) => {
    if (!settings?.modules) {
      return null;
    }
    return settings.modules[moduleName] || null;
  };

  return {
    modules: settings?.modules || {},
    isModuleEnabled,
    getModuleConfig,
    loading,
    error,
    refreshSettings
  };
};

export default useGlobalSettings; 